import { CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTabId } from '@/components/layout/workspace/workspaceContext';
import { useExpansionMemory } from '@/components/layout/workspace/useExpansionMemory';
import { useScrollMemory } from '@/components/layout/workspace/useScrollMemory';
import {
  Button,
  Checkbox,
  EntityChip,
  EntityText,
  HoldToConfirm,
  ObjectPreview,
  Popover,
  RelativeTime,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TimeGrid,
  TimeInterval,
} from '@/components/ui';
import { buildTimeAxis, timePixel, type TimeScale } from '@/lib/timeGrid';
import { zoneOffset } from '@/lib/eventTime';
import { HOME, ANCHOR, GROUPS, NOW, PEOPLE, ROWS, ZONES, type DutyEvent } from './model';
import { useDutyData, type DutyData } from './useDutyData';

type Preferences = { scale: TimeScale; timeZone: string; person: string; periods: number };
const DEFAULT: Preferences = { scale: 'fortnight', timeZone: 'Europe/Moscow', person: 'all', periods: 1 };
function load(key: string): Preferences {
  try {
    const x = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (!x) return DEFAULT;
    return {
      scale: ['day', 'week', 'month', 'fortnight'].includes(x.scale) ? x.scale : DEFAULT.scale,
      timeZone: ZONES.some((z) => z.id === x.timeZone) ? x.timeZone : DEFAULT.timeZone,
      person: x.person === 'all' || PEOPLE.some((p) => p.id === x.person) ? x.person : 'all',
      periods: Number.isInteger(x.periods) && x.periods >= 1 && x.periods <= 4 ? x.periods : 1,
    };
  } catch {
    return DEFAULT;
  }
}
export function DutyExample() {
  const data = useDutyData(),
    navigate = useNavigate();
  return (
    <DutyView
      data={data}
      onDetails={(event) => navigate(HOME + '/event/' + event.id)}
      onPerson={(id) => navigate(HOME + '/person/' + id)}
    />
  );
}
export function DutyView({
  data,
  onDetails,
  onPerson,
}: {
  data: DutyData;
  onDetails?: (event: DutyEvent, trigger: HTMLElement) => void;
  onPerson?: (id: string, trigger: HTMLElement) => void;
}) {
  const tab = useTabId() ?? 'gallery',
    key = 'kaname.duty.preferences.v1.' + tab,
    scrollKey = key + '.scroll';
  const [preferences, setPreferences] = useState(() => load(key));
  const { scale, timeZone, person, periods } = preferences;
  const [expanded, setExpanded] = useExpansionMemory(
    'duty.groups',
    GROUPS.map((g) => g.id),
  );
  const viewport = useRef<HTMLDivElement>(null);
  useScrollMemory(viewport, scrollKey);
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(preferences));
    } catch {
      /* Без памяти. */
    }
  }, [key, preferences]);
  const [selected, setSelected] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [restoreFocus, setRestoreFocus] = useState(true);
  const popupId = useId(),
    event = selected ? data.events.find((e) => e.id === selected.id) : undefined;
  const close = useCallback(() => setSelected(null), []);
  const setOpen = useCallback((open: boolean) => {
    if (!open) setSelected(null);
  }, []);
  const [loading, setLoading] = useState(false),
    [error, setError] = useState<string>(),
    [fail, setFail] = useState(false);
  const timer = useRef<number | undefined>(undefined),
    busy = useRef(false);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const today = useCallback(() => {
    const el = viewport.current;
    if (!el || !el.clientWidth) return;
    const label =
      el.querySelector('[data-row-label], [data-time-header] > div')?.getBoundingClientRect().width ?? 0;
    const axis = buildTimeAxis(scale, ANCHOR, periods, timeZone);
    el.scrollTo({
      left: Math.max(0, timePixel(NOW, axis) - (el.clientWidth - label) / 2),
      behavior: 'instant',
    });
  }, [scale, periods, timeZone]);
  const lastView = useRef({ scale, timeZone }),
    centred = useRef(false);
  const hadSavedScroll = useRef(
    (() => {
      try {
        return !!localStorage.getItem(scrollKey);
      } catch {
        return false;
      }
    })(),
  );
  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    let changed = lastView.current.scale !== scale || lastView.current.timeZone !== timeZone;
    lastView.current = { scale, timeZone };
    const place = () => {
      if (el.clientWidth && ((!centred.current && !hadSavedScroll.current) || changed)) {
        centred.current = true;
        changed = false;
        today();
      }
    };
    const observer = new ResizeObserver(place);
    observer.observe(el);
    place();
    return () => observer.disconnect();
  }, [scale, timeZone, today]);
  const change = (next: Partial<Preferences>) => {
    close();
    window.clearTimeout(timer.current);
    busy.current = false;
    setLoading(false);
    setError(undefined);
    setPreferences((previous) => ({ ...previous, ...next }));
  };
  const loadMore = () => {
    if (busy.current || periods >= 4) return;
    busy.current = true;
    setLoading(true);
    setError(undefined);
    timer.current = window.setTimeout(() => {
      if (fail) {
        setError('Не удалось добавить период. События и место сохранены.');
        setFail(false);
      } else setPreferences((previous) => ({ ...previous, periods: Math.min(4, previous.periods + 1) }));
      busy.current = false;
      setLoading(false);
    }, 1000);
  };
  const anchorRect = useCallback(() => {
    const el = viewport.current,
      anchor = selected?.anchor;
    if (!el || !anchor?.isConnected) return null;
    const r = anchor.getBoundingClientRect(),
      v = el.getBoundingClientRect();
    const label =
      el.querySelector('[data-row-label], [data-time-header] > div')?.getBoundingClientRect().width ?? 0;
    const left = Math.max(r.left, v.left + label, 0),
      right = Math.min(r.right, v.right, window.innerWidth),
      top = Math.max(r.top, v.top + 64, 0),
      bottom = Math.min(r.bottom, v.bottom, window.innerHeight);
    return right > left && bottom > top ? { left, right, top, bottom } : null;
  }, [selected]);
  const zone = ZONES.find((z) => z.id === timeZone)!;
  const linkedPerson = (id: string) => {
    setRestoreFocus(false);
    setSelected(null);
    if (onPerson && selected) onPerson(id, selected.anchor);
  };
  return (
    <div data-slot="duty-example" className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full min-w-0 overflow-x-auto sm:w-auto sm:flex-1">
          <Tabs
            variant="pill"
            value={scale}
            onValueChange={(value) => change({ scale: value as TimeScale, periods: 1 })}
          >
            <TabsList aria-label="Масштаб дежурств">
              <TabsTrigger value="day">День</TabsTrigger>
              <TabsTrigger value="week">Неделя</TabsTrigger>
              <TabsTrigger value="fortnight">2 недели</TabsTrigger>
              <TabsTrigger value="month">Месяц</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <Button size="md" variant="secondary" onClick={today}>
          Сегодня
        </Button>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <p className="mb-2 text-xs text-fg-muted">Часовой пояс</p>
          <Select value={timeZone} onValueChange={(value) => change({ timeZone: value })}>
            <SelectTrigger aria-label="Часовой пояс">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ZONES.map((z) => (
                <SelectItem key={z.id} value={z.id}>
                  {z.name} · {zoneOffset(new Date(NOW), z.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0">
          <p className="mb-2 text-xs text-fg-muted">Участник</p>
          <Select value={person} onValueChange={(value) => change({ person: value })}>
            <SelectTrigger aria-label="Участник">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все участники</SelectItem>
              {PEOPLE.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <TimeGrid
        rows={ROWS}
        shifts={data.events.filter((e) => person === 'all' || e.personId === person)}
        groups={GROUPS}
        expandedGroups={expanded}
        onExpandedGroupsChange={setExpanded}
        scale={scale}
        timeZone={timeZone}
        anchor={ANCHOR}
        periods={periods}
        now={NOW}
        viewportRef={viewport}
        loading={loading}
        error={error}
        hasMore={periods < 4}
        onLoadMore={loadMore}
        label="Группы и слои дежурств — вымышленные данные"
        rowHeading="Слой"
        highlightWeekends
        popup={{ id: popupId, activeShiftId: event?.id }}
        onShiftClick={(shift, anchor) => {
          setRestoreFocus(true);
          setSelected((previous) => (previous?.id === shift.id ? null : { id: shift.id, anchor }));
        }}
      />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-fg-muted">
        <span className="inline-flex items-center gap-2">
          <span aria-hidden className="h-3 w-4 rounded-xs border border-line bg-muted" />
          Выходные
        </span>
        <span className="inline-flex items-center gap-2">
          <span aria-hidden className="h-3 w-4 rounded-xs border border-dashed border-warning-line" />
          Предварительное событие
        </span>
        <Checkbox
          label="Сбой следующей подгрузки"
          checked={fail}
          onCheckedChange={(value) => setFail(value === true)}
        />
      </div>
      <p className="text-xs text-fg-muted">
        Пример на 24 октября 2026 года · {zone.name} · {zoneOffset(new Date(NOW), timeZone)}. Данные
        вымышлены, действия пробные.
      </p>
      <Popover
        id={popupId}
        open={!!event}
        anchor={selected?.anchor ?? null}
        label={event?.title ?? 'Событие'}
        onOpenChange={setOpen}
        returnFocus={restoreFocus}
        getAnchorRect={anchorRect}
      >
        {event && (
          <ObjectPreview
            title={event.title}
            icon={<CalendarDays />}
            status={{
              label: event.tentative ? 'Предварительно' : 'Подтверждено',
              tone: event.tentative ? 'warn' : 'info',
            }}
            onClose={close}
            meta={
              <>
                <span>Автор:</span>
                <EntityText
                  variant="author"
                  name={PEOPLE.find((p) => p.id === event.authorId)?.name ?? 'Участник'}
                  onClick={() => linkedPerson(event.authorId)}
                />
                <RelativeTime at={event.createdAt} now={NOW} timeZone={timeZone} zoneLabel={zone.name} />
              </>
            }
            actions={
              <>
                {event.tentative && (
                  <Button
                    size="sm"
                    onClick={() => {
                      data.confirm(event.id);
                      close();
                    }}
                  >
                    Подтвердить
                  </Button>
                )}
                {onDetails && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setRestoreFocus(false);
                      close();
                      if (selected) onDetails(event, selected.anchor);
                    }}
                  >
                    Подробнее
                  </Button>
                )}
              </>
            }
            destructiveAction={
              <HoldToConfirm
                size="sm"
                hint="удержи"
                onConfirm={() => {
                  setRestoreFocus(false);
                  close();
                  data.remove(event.id);
                  requestAnimationFrame(() => viewport.current?.focus({ preventScroll: true }));
                }}
              >
                Удалить событие
              </HoldToConfirm>
            }
          >
            <div>
              <p className="mb-2 text-xs text-fg-muted">На дежурстве</p>
              <EntityChip
                kind="user"
                name={PEOPLE.find((p) => p.id === event.personId)?.name ?? 'Участник'}
                onClick={() => linkedPerson(event.personId)}
              />
            </div>
            <TimeInterval start={event.start} end={event.end} timeZone={timeZone} zoneLabel={zone.name} />
            {event.awaitingId && (
              <div className="rounded-md bg-muted p-3">
                <p className="mb-2 text-xs text-fg-muted">Ждёт ответа</p>
                <div className="flex flex-wrap items-center gap-2">
                  <EntityChip
                    kind="user"
                    name={PEOPLE.find((p) => p.id === event.awaitingId)?.name ?? 'Участник'}
                    onClick={() => linkedPerson(event.awaitingId!)}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-disabled={data.reminded.has(event.id)}
                    onClick={() => data.remind(event.id)}
                  >
                    {data.reminded.has(event.id) ? 'Напоминание отправлено' : 'Напомнить'}
                  </Button>
                </div>
              </div>
            )}
          </ObjectPreview>
        )}
      </Popover>
    </div>
  );
}
